import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Grid, Card, CardContent, Typography, Button, TextField, MenuItem } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import ImageUploader from './ImageUploader';
import Alert from '@mui/material/Alert';
import { getAuthHeaders, getAuthHeader, getUploadApiBaseUrl } from '../../utils/api';


const CreateLog = () => {
  const { userId } = useAuth();
  const { currentLogbookId } = useLogbook();
  const [title, setTitle] = useState('');
  const [year, setYear] = useState('');
  const [departureDateTime, setDepartureDateTime] = useState('');
  const [arrivalDateTime, setArrivalDateTime] = useState('');
  const [windDirection, setWindDirection] = useState('');
  const [windSpeed, setWindSpeed] = useState('');
  const [distance, setDistance] = useState('');
  const [position, setPosition] = useState('');
  const [description, setDescription] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { currentUser } = useAuth();
  const [showLoginMessage, setShowLoginMessage] = useState(false); // Statiskt tillstånd för att visa eller dölja meddelandet
  const [createError, setCreateError] = useState(null); // 403 t.ex. basplansgräns
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser === null) {
      navigate('/info');
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Kontrollera om användaren är inloggad innan du fortsätter med att skapa loggen
    if (!currentUser) {
      // Visa meddelandet om användaren inte är inloggad
      setShowLoginMessage(true);
      return;
    }

    // Återställ meddelandet om användaren är inloggad och göra resten av din kod för att hantera loggskapandet
    setShowLoginMessage(false);
    setCreateError(null);
    // ...

    const formData = new FormData();
    selectedFiles.forEach((file, index) => {
      formData.append(`file${index}`, file);
    });
    formData.append('accountId', userId);
    formData.append('type', 'triplog');

    const uploadResponse = await fetch(`${getUploadApiBaseUrl()}/api/uploads`, {
      method: 'POST',
      headers: getAuthHeader() ? { Authorization: getAuthHeader() } : {},
      body: formData
    });

    if (!uploadResponse.ok) {
      const errData = await uploadResponse.json().catch(() => ({}));
      alert(errData.message || 'Kunde inte ladda upp bilder.');
      return;
    }
    const uploadedImages = await uploadResponse.json();
    console.log('Uploaded images: ', uploadedImages);
    // Skapa ett unikt slumptal
    const _id = (Math.floor(Math.random() * 1e12) + 1e12).toString().substring(1);
    console.log('_id:', _id);  // Lägg till denna rad

    if (!currentLogbookId) {
      alert('Välj en loggbok i headern innan du skapar en logg.');
      return;
    }
    const logData = {
      _id,
      logbookId: currentLogbookId,
      title,
      year,
      departureDateTime,
      arrivalDateTime,
      windDirection,
      windSpeed,
      distance,
      position,
      description,
      uploadedImages
    };
    if (logData.distance === '') {
      logData.distance = null;
    }
    // skicka till servern
    try {
      const createLogResponse = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/createlog`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(logData)
      });

      if (!createLogResponse.ok) {
        const errorText = await createLogResponse.text();
        console.error('Error creating log:', createLogResponse.status, errorText);
        if (createLogResponse.status === 401) {
          alert('Du är inte inloggad. Logga in igen.');
          navigate('/info');
          return;
        }
        if (createLogResponse.status === 403) {
          let msg = errorText;
          try {
            const errData = JSON.parse(errorText);
            if (errData.message) msg = errData.message;
          } catch (_) {}
          setCreateError(msg);
          return;
        }
        alert(`Fel vid skapande av logg: ${createLogResponse.status}. ${errorText}`);
        return;
      }

      const responseData = await createLogResponse.json();
      navigate(`/showlog/${responseData._id}`);

    } catch (error) {
      console.error('Error:', error);
      alert(`Ett fel uppstod: ${error.message}`);
    }
  };


  return (
    <Box sx={{ maxWidth: '1200px', margin: '0 auto', padding: { xs: 2, md: 4 } }}>
      <Card>
        <CardContent>
        {showLoginMessage && (
          <Alert severity="warning" onClose={() => setShowLoginMessage(false)}>
            Du måste vara inloggad för att skapa en ny logg.
          </Alert>
        )}
        {createError && (
          <Alert severity="error" onClose={() => setCreateError(null)} sx={{ mb: 2 }}>
            {createError}
            <Box sx={{ mt: 1 }}>
              <Button component={Link} to="/subscription" variant="outlined" size="small">
                Gå till Prenumerationsplaner
              </Button>
            </Box>
          </Alert>
        )}
        <Typography variant="h5">Ny logg</Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Rubrik"
                value={title}
                onChange={e => setTitle(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="År"
                type="number"
                value={year}
                onChange={e => setYear(e.target.value)}
                fullWidth
                inputProps={{ min: 1900, max: new Date().getFullYear() }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Avgångstid"
                type="datetime-local"
                value={departureDateTime}
                onChange={e => setDepartureDateTime(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />

            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ankomsttid"
                type="datetime-local"
                value={arrivalDateTime}
                onChange={e => setArrivalDateTime(e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Vindriktning"
                value={windDirection}
                onChange={e => setWindDirection(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Vindstyrka (m/s)"
                value={windSpeed}
                onChange={e => setWindSpeed(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Distans (nm)"
                type="number"
                value={distance}
                onChange={e => setDistance(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Från - Till"
                value={position}
                onChange={e => setPosition(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Beskrivning"
                value={description}
                onChange={e => setDescription(e.target.value)}
                multiline
                rows={4}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <ImageUploader onUploadSuccess={(files) => setSelectedFiles(prev => [...prev, ...files])} />
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' }}>
                    {uploadedImages.map((imageUrl, index) => (
                      <img key={index} src={imageUrl} alt="Uppladdad bild" style={{ width: '30%', height: 'auto', margin: '1%' }} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit"
                variant="contained"
                color="primary">
                Skapa logg
              </Button>
            </Grid>
          </Grid>
        </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateLog;